
import React from 'react';
import '../../../../scss/components/_list-header.scss';
import {
  authDocDispatchMethod,
  authDocType,
  createCommentSuccesMessages,
} from '@finpay-development/shared-platform/lib/esm/shared/enums';
import {patientService} from '@finpay-development/shared-platform/lib/esm/patient/services/patient-service';
import {
  showErrorStatus,
  showStatus,
} from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../../shared/state/store';
import {
  createPatientDocument, createPatientNote,
  deletePatientDocument, downloadHelloSignDocument, downloadS3Document,
} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import {RootState} from '../../../../shared/state/root-reducer';
import {FormikValues} from 'formik';
import {RolePageNames} from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import {Button, DocumentsAccordion} from '@finpay-development/shared-components';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';

import { PatientDocument } from '@finpay-development/shared-platform/lib/esm/patient/components/patient-drilldown/documents/documents-accordion-contents-v2-types';
export type { PatientDocument } from '@finpay-development/shared-platform/lib/esm/patient/components/patient-drilldown/documents/documents-accordion-contents-v2-types';

interface DocumentAccordionContentsProps {
  documents: PatientDocument[];
  patientId: number;
  patientEncounterId: number;
}



export const DocumentsAccordionContentsV2 = (props: DocumentAccordionContentsProps) => {

  const {
    documents = [],
    patientId,
    patientEncounterId,
  } = props;

  const dispatch = useDispatch<AppDispatch>();

  const stateFields = {
    loading: useSelector((state: RootState) => {
      return state.patientContext.isLoading;
    }),
    userEmail: useSelector((state: RootState) => {
      return state.userContext.userProfile.userName
    }),
  };

  const {loading, userEmail} = stateFields;

  //These buttons are only visible for no-sig docs
  const handleButtonOnClick = async (
        docType: authDocType,
        paymentId: number | null,
        dispatchMethod: authDocDispatchMethod,
        authorizationDocumentStatusId: number
) => {

    let apiResponse;

    switch (docType) {
      case authDocType.inst:
        apiResponse = await patientService.installmentDocument(
            patientEncounterId,
            dispatchMethod,
            authorizationDocumentStatusId
        );
        break;
      case authDocType.spa:
        apiResponse = await patientService.spaDocument(
            patientEncounterId,
            dispatchMethod,
            authorizationDocumentStatusId
        );
        break;
      case authDocType.otp:
        if(!paymentId) {
          dispatch(showStatus('Could not send One Time Payment document due to missing paymentId'));
          return
        }
        apiResponse = await patientService.otpDocument({
              encounterId : patientEncounterId,
              paymentId: paymentId,
              dispatchMethod
            });
        break;
      case authDocType.phi:
        apiResponse = await patientService.phiDocument({
          encounterId : patientEncounterId,
          dispatchMethod,
          authDocStatusId: authorizationDocumentStatusId
        })
        break;
    }

    if (apiResponse?.hasErrors) {
      dispatch(showErrorStatus(
          apiResponse?.entity?.message || apiResponse.errorMessage));
    } else {
      let currentDate = new Date().toISOString()
      dispatch(createPatientNote({
        message: createCommentSuccesMessages.comment,
        comment: {
          patientNotesId: 0,
          patientId,
          patientEncounterId,
          noteText: `${getDocumentName(docType)} re-sent via ${dispatchMethod} by ${userEmail}`,
          noteDt: currentDate,
          completionDueDt: currentDate
        },
        showNotify: false
      }))
      dispatch(showStatus('Authorization document sent successfully'));
    }

  };

  const handleDownloadS3DocumentOnClick = (
    patientId: number,
    encounterId: number,
    documentName: string
  ) => {
    dispatch(
        downloadS3Document({
          patientId,
          encounterId,
          s3DocumentName: documentName
        }))
  }

  const handleDownloadHelloSignDocumentOnClick = (
      patientId: number,
      encounterId: number,
      externalDocumentId: string) => {

    dispatch(
        downloadHelloSignDocument({
          patientId,
          encounterId,
          externalDocumentId,
        })
    );

  }

  const handleDocumentUploadOnClick = async (formValues: FormikValues) => {

    //Include this check in the shared components

    const isPdf =
        formValues.file?.type === 'application/pdf' ||
        formValues.file?.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      dispatch(showErrorStatus('Only PDF files are allowed for now'));
      return;
    }

    const abortController = new AbortController();
    await dispatch(createPatientDocument({
      patientId: formValues.patientID,
      encounterId: formValues.encounterId,
      file: formValues.file,
      cancelSignal: abortController.signal,
    }));
  }

  const handleDocumentDeleteOnClick = (selectDocumentId: number) => () => {
    const abortController = new AbortController();
    dispatch(deletePatientDocument({
      encounterId: patientEncounterId,
      patientDocumentId: selectDocumentId,
      cancelSignal: abortController.signal,
    }));
  }

  return (
      <DocumentsAccordion
          handleDownloadS3DocumentOnClick={handleDownloadS3DocumentOnClick}
          handleDownloadHelloSignDocumentOnClick={handleDownloadHelloSignDocumentOnClick}
          isPatientDocumentsLoading={loading.patientDocuments}
          documents={documents}
          handleButtonOnClick={handleButtonOnClick}
          handleDocumentUploadOnClick={handleDocumentUploadOnClick}
          handleDocumentDeleteOnClick={handleDocumentDeleteOnClick}
          patientId={patientId}
          patientEncounterId={patientEncounterId}
          accessControlRenderProp={(AddDocumentButton: JSX.Element)=>{
            return (
                <AccessControl
                    rolePageName={RolePageNames.PatientRecords}
                    actionName="Add Instance of Care"
                    renderNoAccess={() => <Button disabled>Add Document</Button>}
                >
                  {AddDocumentButton}
                </AccessControl>
            )
          }}
          dispatchStatus={(message: string, isError: boolean) => {
            dispatch(isError ? showErrorStatus(message) : showStatus(message))
          }}
          />

  );
};

const getDocumentName = (typeOfDoc?: string) => {
  switch (typeOfDoc) {
    case 'inst' :
      return 'Installment Agreement';
    case 'spa' :
      return 'Single Pay Agreement';
    case 'pfr' :
      return 'PFR Adjustment/Refinance Agreement';
    case 'phi' :
      return 'PHI Consent';
    case 'otp' :
      return 'One Time Payment Agreement';
    default :
      return '';
  }
};
